import { FC, useCallback, useMemo, useState } from 'react';

import { cloneDeep, isEqual } from 'lodash';

import { Button, JsonTree, Modal, PermissionAccess } from '@components/index';
import { UserPermissions } from '@enums/user-permissions';
import { useUpdateBrandAsset } from '@pages/home-page/hooks/use-update-brand-asset';

type Props = {
  isOpen: boolean;
  metadata?: Record<string, any>;
  assetName?: string;
  assetId?: number;
  thumbnailUrl?: string;
  isLocked?: boolean;
  handleCancel: () => void;
};

export const MetadataModal: FC<Props> = ({
  isOpen,
  metadata,
  assetId,
  assetName,
  thumbnailUrl = null,
  isLocked = false,
  handleCancel,
}) => {
  // The react-editable-json-tree library mutates the original data prop directly
  // We need to create new references to ensure React detects state changes and triggers re-renders
  const originalMetadata = cloneDeep(metadata);
  const [customMetadata, setCustomMetadata] = useState<Record<string, any>>(
    originalMetadata || {},
  );

  const { mutate: updateBrandAsset, isLoading: isUpdating } =
    useUpdateBrandAsset();

  const handleDataChange = (updatedData: Record<string, any>) => {
    // Clone the updated data to create a new object reference
    const updatedMetadata = cloneDeep(updatedData);
    setCustomMetadata(updatedMetadata);
  };

  const checkIfDataIsChanged = useCallback(() => {
    const hasChanged = !isEqual(originalMetadata || {}, customMetadata);

    return hasChanged;
  }, [originalMetadata, customMetadata]);

  const handleCloseModal = useCallback(() => {
    setCustomMetadata({});
    handleCancel();
  }, [handleCancel]);

  const handleSaveClick = () => {
    if (assetId) {
      // Use the API hook to update the asset
      updateBrandAsset({
        assetId,
        externalAssetMetadata: customMetadata,
        onSuccess: handleCloseModal,
      });
    }
  };

  const isSaveDisabled = useMemo(() => {
    return isUpdating || !checkIfDataIsChanged() || isLocked;
  }, [isUpdating, checkIfDataIsChanged, customMetadata, isLocked]);

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal}>
      <div className="p-8 w-[600px] flex flex-col gap-4">
        <div className="text-lg font-medium">Metadata</div>
        <div className="flex gap-3 items-center">
          {thumbnailUrl && (
            <div className="rounded h-10 w-10 bg-gray-2">
              <img
                src={thumbnailUrl}
                alt={`${assetName} thumbnail`}
                className="rounded w-full h-full"
              />
            </div>
          )}
          <div className="text-gray-1 text-sm">{assetName}</div>
        </div>
        <PermissionAccess
          permission={UserPermissions.MANAGE_BRAND_LIBRARY}
          AllowedContent={
            <>
              <JsonTree
                data={customMetadata}
                readOnly={isLocked}
                onUpdate={customMetadata => handleDataChange(customMetadata)}
              />
              {!isLocked && (
                <div className="sticky bottom-0 left-0 flex justify-between">
                  <Button
                    variant="outlined"
                    label="Cancel"
                    onClick={handleCloseModal}
                    classOverride="!w-[260px]"
                  />
                  <Button
                    variant="contained"
                    label="Save metadata"
                    onClick={handleSaveClick}
                    isDisabled={isSaveDisabled}
                    isLoading={isUpdating}
                    classOverride="!w-[260px]"
                  />
                </div>
              )}
            </>
          }
          DeniedContent={<JsonTree data={customMetadata} readOnly={true} />}
        />
      </div>
    </Modal>
  );
};
